import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import api from '../../lib/api';
import { Input } from '../../components/ui/input';
import { ConfirmButton } from '../StaffAppItemDetail';
import ScanOrType from './ScanOrType';
import StocktakeCountSheet from './StocktakeCountSheet';
import StocktakeApprovalPanel from './StocktakeApprovalPanel';
import ToolHeader, { Spinner } from './ToolHeader';
import { Link } from 'react-router-dom';

export default function StocktakeCountPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { tools } = useOutletContext();
  const [session, setSession] = useState(null);
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');
  const [view, setView] = useState('todo');

  const load = () => api.get(`/stocktake/sessions/${sessionId}`).then((r) => setSession(r.data));
  useEffect(() => { if (session?.status === 'pending_approval') setView('done'); }, [session?.status]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => toast.error('Could not load this count')); }, [sessionId]);

  const list = useMemo(() => {
    if (!session) return [];
    const f = filter.toLowerCase();
    return session.items.filter((i) => (view === 'todo' ? i.counted_quantity == null : i.counted_quantity != null)
      && (!f || i.product_name.toLowerCase().includes(f))).slice(0, 80);
  }, [session, filter, view]);

  if (!session) return <><ToolHeader title="Stock take" back="/staff-app/tools/stocktake" /><Spinner /></>;
  const editable = session.status === 'in_progress';
  const counted = session.items.filter((i) => i.counted_quantity != null).length;

  const onCode = async (code) => {
    try {
      const { data } = await api.get('/staff-app/products/lookup', { params: { q: code } });
      const ids = new Set(data.products.map((p) => p.product_id));
      const match = session.items.filter((i) => ids.has(i.product_id));
      if (match.length === 1) setActive(match[0]);
      else if (match.length > 1) { setFilter(code); setView(match.some((m) => m.counted_quantity == null) ? 'todo' : 'done'); }
      else toast.error(`"${code}" is not part of this count`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Lookup failed'); }
  };

  const saveCount = async (item, total, batchEntries) => {
    setSaving(true);
    try {
      await api.post(`/stocktake/sessions/${sessionId}/entry`, { items: [{ product_id: item.product_id, counted_quantity: total, batch_entries: batchEntries }] });
      toast.success(`${item.product_name}: ${total} counted`);
      setActive(null);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save count'); } finally { setSaving(false); }
  };

  const submit = async () => {
    try {
      await api.post(`/stocktake/sessions/${sessionId}/submit`);
      toast.success('Sent for admin approval');
      navigate('/staff-app/tools/stocktake');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not submit'); }
  };

  return (
    <div data-testid="staff-app-stocktake-count">
      <ToolHeader title={(session.filters?.categories || []).join(', ') || 'Whole-store count'} back="/staff-app/tools/stocktake"
        subtitle={`${counted}/${session.total_products} counted${editable ? '' : ' · awaiting approval'}`} />
      <div className="h-1 bg-muted"><div className="h-1 bg-primary transition-[width] duration-500" style={{ width: `${(counted / Math.max(1, session.total_products)) * 100}%` }} /></div>
      <div className="p-4 space-y-3">
        <Link to="/staff-app/tools/pack-counts" className="block text-sm underline" data-testid="stocktake-opened-pack-counts">Opened-pack counts & approved adjustments</Link>
        {session.status === 'pending_approval' && tools?.stocktake_approve && <StocktakeApprovalPanel sessionId={sessionId} onChanged={load} />}
        {editable && <ScanOrType onCode={onCode} testId="staff-app-stocktake-scan" />}
        <div className="flex gap-2">
          {['todo', 'done'].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`staff-app-stocktake-view-${v}`}
              className={`flex-1 text-xs font-semibold py-2 rounded-lg border ${view === v ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}>
              {v === 'todo' ? `To count (${session.total_products - counted})` : `Counted (${counted})`}
            </button>
          ))}
        </div>
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name" className="h-10" data-testid="staff-app-stocktake-filter" />
        <div className="divide-y divide-border rounded-xl border border-border bg-card" data-testid="staff-app-stocktake-items">
          {list.length === 0 && <p className="px-3.5 py-6 text-center text-sm text-muted-foreground">Nothing here.</p>}
          {list.map((i) => (
            <button key={i.product_id} disabled={!editable} onClick={() => setActive(i)} data-testid={`staff-app-stocktake-item-${i.product_id}`}
              className="w-full flex items-center gap-3 px-3.5 py-3 text-left hover:bg-muted/50 disabled:opacity-70">
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold truncate">{i.product_name}</span>
                <span className="block text-xs text-muted-foreground truncate">{i.category}{i.bin_location ? ` · Bin ${i.bin_location}` : ''}</span>
              </span>
              {i.counted_quantity != null && <span className="flex items-center gap-1 text-sm font-bold text-primary"><CheckCircle2 className="w-4 h-4" />{i.counted_quantity}</span>}
            </button>
          ))}
        </div>
        {editable && counted > 0 && (
          <div className="flex pt-1">
            <ConfirmButton label="Submit for approval" title="Submit this count?" testId="staff-app-stocktake-submit" onConfirm={submit}
              description={`${counted} of ${session.total_products} products counted. You can't change counts after submitting; an admin will review the variances before stock is updated.`} />
          </div>
        )}
      </div>
      <StocktakeCountSheet item={active} onClose={() => setActive(null)} onSave={saveCount} saving={saving} />
    </div>
  );
}
